import { createEmitter } from "../Utilities/events";
import { MediaType, detectMediaType } from "../Core/MediaTypes";
import { StreamingProviderEvents } from "./StreamingProvider";
import {
  SurfaceKind,
  PlaybackProviderEvents,
} from "../Providers/Playback/PlaybackProviderContract";
import { extractYouTubeId } from "../Providers/Playback/embedIds";
import { tpgTrace } from "../production/tpgPipelineTrace";

var ytApiPromise = null;

function loadYouTubeIframeApi() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("YouTube API requires window"));
  }
  if (window.YT && window.YT.Player) {
    return Promise.resolve(window.YT);
  }
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise(function (resolve, reject) {
    var prior = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      if (typeof prior === "function") {
        try {
          prior();
        } catch (e) {
          /* ignore */
        }
      }
      resolve(window.YT);
    };
    if (!document.getElementById("ngp-youtube-iframe-api")) {
      var tag = document.createElement("script");
      tag.id = "ngp-youtube-iframe-api";
      tag.src = "https://www.youtube.com/iframe_api";
      tag.onerror = function () {
        ytApiPromise = null;
        reject(new Error("Failed to load YouTube iframe API"));
      };
      var first = document.getElementsByTagName("script")[0];
      first.parentNode.insertBefore(tag, first);
    }
  });
  return ytApiPromise;
}

/**
 * YouTube embed PlaybackProvider — iframe API behind the unified contract.
 * Pentecost+ chrome stays outside the iframe; YouTube may still show mandated UI.
 */
export function createYouTubeEmbedProvider() {
  var emitter = createEmitter();
  var container = null;
  var player = null;
  var hostEl = null;
  var name = "youtube-embed";
  var pollTimer = null;
  var lastMuted = true;
  var lastVolume = 100;

  function stopPoll() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function startPoll() {
    stopPoll();
    pollTimer = setInterval(function () {
      if (!player || typeof player.getCurrentTime !== "function") return;
      try {
        var t = player.getCurrentTime() || 0;
        var d =
          typeof player.getDuration === "function"
            ? player.getDuration() || 0
            : 0;
        emitter.emit(PlaybackProviderEvents.TIME, {
          currentTime: t,
          duration: d,
        });
        emitter.emit(PlaybackProviderEvents.PROGRESS, {
          currentTime: t,
          duration: d,
        });
      } catch (e) {
        /* ignore */
      }
    }, 250);
  }

  return {
    getName: function () {
      return name;
    },
    getSurfaceKind: function () {
      return SurfaceKind.EMBED;
    },
    initialize: function () {
      return loadYouTubeIframeApi().then(function () {});
    },
    supports: function (source) {
      if (!source || !source.src) return false;
      var type = source.type || detectMediaType(source.src);
      return type === MediaType.YOUTUBE || !!extractYouTubeId(source.src);
    },
    attach: function (el) {
      container = el;
      if (container) {
        container.innerHTML = "";
        hostEl = document.createElement("div");
        hostEl.className = "ngp-embed-host ngp-embed-host--youtube";
        container.appendChild(hostEl);
      }
    },
    detach: function () {
      stopPoll();
      if (player && typeof player.destroy === "function") {
        try {
          player.destroy();
        } catch (e) {
          /* ignore */
        }
      }
      player = null;
      if (container) container.innerHTML = "";
      hostEl = null;
      container = null;
    },
    load: function (source, options) {
      options = options || {};
      var id = extractYouTubeId(source && source.src);
      if (!id) {
        return Promise.reject({
          code: "YOUTUBE_ID",
          message: "Could not parse YouTube id",
          recoverable: false,
        });
      }
      if (!hostEl) {
        return Promise.reject(new Error("youtube-embed: not attached"));
      }
      var startMuted =
        options.muted != null ? !!options.muted : true;
      lastMuted = startMuted;
      if (options.volume != null) {
        lastVolume = Math.round(
          Math.max(0, Math.min(1, Number(options.volume) || 1)) * 100
        );
      }

      tpgTrace("MAIN_PROVIDER_CREATED", {
        provider: name,
        id: id,
        muted: startMuted,
        volume: lastVolume,
      });
      if (startMuted) {
        tpgTrace("PROVIDER_MUTED", {
          provider: name,
          reason: "load_startMuted",
          lastMuted: true,
        });
      }

      return loadYouTubeIframeApi().then(function (YT) {
        return new Promise(function (resolve, reject) {
          if (player && typeof player.destroy === "function") {
            try {
              player.destroy();
            } catch (e) {
              /* ignore */
            }
            player = null;
          }
          hostEl.innerHTML = "";
          var mount = document.createElement("div");
          hostEl.appendChild(mount);

          player = new YT.Player(mount, {
            videoId: id,
            width: "100%",
            height: "100%",
            playerVars: {
              autoplay: 0,
              controls: 0,
              modestbranding: 1,
              rel: 0,
              playsinline: 1,
              enablejsapi: 1,
              origin:
                typeof window !== "undefined" ? window.location.origin : undefined,
            },
            events: {
              onReady: function () {
                try {
                  if (startMuted && player.mute) player.mute();
                  else if (!startMuted && player.unMute) player.unMute();
                  if (typeof player.setVolume === "function") {
                    player.setVolume(lastVolume);
                  }
                } catch (e) {
                  /* ignore */
                }
                tpgTrace("PROVIDER_READY", {
                  provider: name,
                  lastMuted: lastMuted,
                  lastVolume: lastVolume / 100,
                  method: startMuted ? "mute" : "unMute",
                });
                startPoll();
                emitter.emit(PlaybackProviderEvents.READY, { id: id });
                emitter.emit(StreamingProviderEvents.MANIFEST_PARSED, {
                  levels: [],
                  currentLevel: -1,
                });
                resolve();
              },
              onStateChange: function (ev) {
                var YTS = YT.PlayerState;
                if (ev.data === YTS.PLAYING) {
                  tpgTrace("PROVIDER_PLAY", {
                    provider: name,
                    muted: lastMuted,
                  });
                  tpgTrace("FIRST_FRAME", {
                    provider: name,
                    surface: "youtube-iframe",
                  });
                  emitter.emit(PlaybackProviderEvents.PLAY);
                } else if (ev.data === YTS.PAUSED) {
                  emitter.emit(PlaybackProviderEvents.PAUSE);
                } else if (ev.data === YTS.ENDED) {
                  emitter.emit(PlaybackProviderEvents.ENDED);
                } else if (ev.data === YTS.BUFFERING) {
                  emitter.emit(PlaybackProviderEvents.BUFFER);
                }
              },
              onError: function (ev) {
                var err = {
                  code: "YOUTUBE_" + (ev && ev.data),
                  message: "YouTube playback error",
                  recoverable: false,
                };
                emitter.emit(PlaybackProviderEvents.ERROR, err);
                emitter.emit(StreamingProviderEvents.ERROR, err);
                reject(err);
              },
            },
          });
        });
      });
    },
    play: function () {
      if (!player || typeof player.playVideo !== "function") {
        return Promise.reject(new Error("youtube not ready"));
      }
      try {
        player.playVideo();
      } catch (e) {
        return Promise.reject(e);
      }
      // playVideo() is fire-and-forget; detect silent autoplay blocks so the
      // shell can mute + retry (natural intro→main handoff has no gesture).
      return new Promise(function (resolve, reject) {
        var attempts = 0;
        var check = function () {
          attempts += 1;
          try {
            var st =
              typeof player.getPlayerState === "function"
                ? player.getPlayerState()
                : -1;
            // 1 playing, 3 buffering — treat as started
            if (st === 1 || st === 3) {
              resolve();
              return;
            }
            // 0 ended immediately is odd but not an autoplay block
            if (st === 0) {
              resolve();
              return;
            }
          } catch (e) {
            resolve();
            return;
          }
          if (attempts >= 6) {
            reject(new Error("youtube autoplay blocked"));
            return;
          }
          setTimeout(check, 100);
        };
        setTimeout(check, 120);
      });
    },
    pause: function () {
      if (player && typeof player.pauseVideo === "function") {
        player.pauseVideo();
      }
    },
    seek: function (t) {
      if (!player || typeof player.seekTo !== "function" || !isFinite(t) || t <= 0) {
        return;
      }
      // Live broadcasts reject arbitrary seeks ("live stream recording is not
      // available"). Duration is 0 / NaN / Infinity while the stream is live.
      try {
        var duration =
          typeof player.getDuration === "function" ? player.getDuration() : 0;
        if (!isFinite(duration) || duration <= 0) {
          return;
        }
        if (t >= duration) {
          return;
        }
      } catch (e) {
        return;
      }
      player.seekTo(t, true);
      emitter.emit(PlaybackProviderEvents.SEEK, { currentTime: t });
    },
    setMuted: function (m) {
      lastMuted = !!m;
      if (!player) {
        tpgTrace(lastMuted ? "PROVIDER_MUTED" : "PROVIDER_UNMUTED", {
          provider: name,
          reason: "engine_setMuted_player_null",
          lastMuted: lastMuted,
        });
        return;
      }
      try {
        if (m && player.mute) player.mute();
        else if (!m && player.unMute) player.unMute();
      } catch (e) {
        /* ignore */
      }
      tpgTrace(lastMuted ? "PROVIDER_MUTED" : "PROVIDER_UNMUTED", {
        provider: name,
        reason: "engine_setMuted",
        method: m ? "mute()" : "unMute()",
        lastMuted: lastMuted,
        playerReady: true,
      });
      emitter.emit(PlaybackProviderEvents.VOLUME, {
        muted: lastMuted,
        volume: lastVolume / 100,
      });
    },
    setVolume: function (v) {
      lastVolume = Math.round(Math.max(0, Math.min(1, v)) * 100);
      if (player && typeof player.setVolume === "function") {
        try {
          player.setVolume(lastVolume);
        } catch (e) {
          /* ignore */
        }
      }
      tpgTrace("PROVIDER_VOLUME", {
        provider: name,
        volume: lastVolume / 100,
        muted: lastMuted,
        reason: "engine_setVolume",
      });
      emitter.emit(PlaybackProviderEvents.VOLUME, {
        muted: lastMuted,
        volume: lastVolume / 100,
      });
    },
    getCurrentTime: function () {
      try {
        return player && player.getCurrentTime ? player.getCurrentTime() : 0;
      } catch (e) {
        return 0;
      }
    },
    getDuration: function () {
      try {
        return player && player.getDuration ? player.getDuration() : 0;
      } catch (e) {
        return 0;
      }
    },
    isPlaying: function () {
      try {
        return (
          player &&
          window.YT &&
          player.getPlayerState() === window.YT.PlayerState.PLAYING
        );
      } catch (e) {
        return false;
      }
    },
    isMuted: function () {
      try {
        if (player && typeof player.isMuted === "function") {
          return !!player.isMuted();
        }
      } catch (e) {
        /* ignore */
      }
      return lastMuted;
    },
    on: emitter.on,
    off: emitter.off,
    destroy: function () {
      this.detach();
      emitter.emit(PlaybackProviderEvents.DESTROYED);
      emitter.emit(StreamingProviderEvents.DESTROYED);
      emitter.removeAllListeners();
    },
  };
}

export default createYouTubeEmbedProvider;
